import { useEffect, useState } from 'react'

/** The picture of the scene as it lies on the marks layer: its left top corner and size, px. */
export type SceneFrame = { x0: number; y0: number; w: number; h: number; width: number; height: number }

type Rect = { left: number; top: number; width: number; height: number }
export type SceneObstacle = { left: number; top: number; right: number; bottom: number }

/** The picture fills its box like `object-fit: cover` centred: the frame of the
 *  whole picture relative to the layer. A picture that is not loaded yet
 *  (no natural size) lies exactly on its box. */
export function coverFrame(box: Rect, natural: { w: number; h: number }, layer: Rect): SceneFrame {
  const s = natural.w > 0 && natural.h > 0 ? Math.max(box.width / natural.w, box.height / natural.h) : 0
  const w = s ? natural.w * s : box.width
  const h = s ? natural.h * s : box.height
  return { x0: box.left + (box.width - w) / 2 - layer.left, y0: box.top + (box.height - h) / 2 - layer.top, w, h, width: layer.width, height: layer.height }
}

/** Keeps a mark whole on the layer and under the name of the place: its plank above the point and its ring on it. */
const EDGE_MARGIN = 24
const EDGE_TOP = 150
const EDGE_BOTTOM = 18

/** The point (x, y — % of the picture) on the layer, px, kept inside the layer. */
export function scenePoint(f: SceneFrame, x: number, y: number, width = 340): { left: number; top: number } {
  const left = f.x0 + (x / 100) * f.w
  const top = f.y0 + (y / 100) * f.h
  return {
    left: insideX(left, width, f.width),
    top: Math.min(Math.max(top, EDGE_TOP), Math.max(f.height - EDGE_BOTTOM, EDGE_TOP)),
  }
}

/** A mark on the layer: its point and the width of its plank, px. */
export type Placed = { id: string; left: number; top: number; width: number }

/** Planks closer than this in height overlap. */
const PLANK_H = 80
const MARK_TOP = 80
const MARK_BOTTOM = 18
const OBSTACLE_GAP = 12
const insideX = (left: number, width: number, available: number) => {
  const edge = Math.min(width / 2 + EDGE_MARGIN, available / 2)
  return Math.max(edge, Math.min(left, available - edge))
}

const meets = (m: Placed, o: SceneObstacle) =>
  m.left + m.width / 2 > o.left && m.left - m.width / 2 < o.right &&
  m.top + MARK_BOTTOM > o.top && m.top - MARK_TOP < o.bottom

/** Marks whose planks would overlap are moved apart: the lower one stays on
 *  its point, the next one goes above it (below, when there is no room above). */
export function unclutter(marks: Placed[], obstacles: SceneObstacle[] = [], width = Number.POSITIVE_INFINITY): Record<string, { left: number; top: number }> {
  const done: Placed[] = []
  for (const m of [...marks].sort((a, b) => b.top - a.top || a.left - b.left)) {
    const at = { ...m, left: insideX(m.left, m.width, width) }
    const hit = () => done.find(d => Math.abs(d.left - at.left) < (d.width + at.width) / 2 + 8 && Math.abs(d.top - at.top) < PLANK_H)
    for (let guard = 0; guard < (marks.length + obstacles.length) * 3; guard++) {
      const obstacle = obstacles.find(o => meets(at, o))
      if (obstacle) {
        const right = obstacle.right + OBSTACLE_GAP + at.width / 2
        const left = obstacle.left - OBSTACLE_GAP - at.width / 2
        const canRight = right + at.width / 2 + EDGE_MARGIN <= width
        const canLeft = left - at.width / 2 >= EDGE_MARGIN
        if (canRight && (!canLeft || Math.abs(right - at.left) <= Math.abs(left - at.left))) at.left = right
        else if (canLeft) at.left = left
        else at.top = obstacle.bottom + OBSTACLE_GAP + MARK_TOP
        continue
      }
      const d = hit()
      if (!d) break
      at.top = d.top - PLANK_H >= EDGE_TOP ? d.top - PLANK_H : Math.max(at.top, d.top) + PLANK_H
    }
    done.push(at)
  }
  return Object.fromEntries(done.map(d => [d.id, { left: d.left, top: d.top }]))
}

/** A crowded/short scene uses the same scrollable list as a phone, rather than losing actions. */
export function fitsScene(marks: Placed[], frame: SceneFrame): boolean {
  return marks.every((m, i) => m.left - m.width / 2 >= EDGE_MARGIN &&
    m.left + m.width / 2 <= frame.width - EDGE_MARGIN && m.top >= EDGE_TOP && m.top + EDGE_BOTTOM <= frame.height &&
    marks.slice(i + 1).every(other => Math.abs(other.left - m.left) >= (other.width + m.width) / 2 + 8 || Math.abs(other.top - m.top) >= PLANK_H))
}

/** A fixed card over the scene becomes an obstacle in the marks layer. */
export function useSceneObstacle(node: HTMLElement | null, layer: HTMLElement | null): SceneObstacle | null {
  const [rect, setRect] = useState<SceneObstacle | null>(null)
  useEffect(() => {
    if (!node || !layer) {
      setRect(null)
      return
    }
    const measure = () => {
      const n = node.getBoundingClientRect()
      const l = layer.getBoundingClientRect()
      setRect({ left: n.left - l.left, top: n.top - l.top, right: n.right - l.left, bottom: n.bottom - l.top })
    }
    const ro = new ResizeObserver(measure)
    ro.observe(node)
    ro.observe(layer)
    window.addEventListener('resize', measure)
    const raf = requestAnimationFrame(measure)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', measure)
      ro.disconnect()
    }
  }, [node, layer])
  return rect
}

/** The frame of the shell's scene picture over the marks layer. Follows
 *  resizes, every new picture and the picture element itself (the shell may
 *  draw it after the layer). Null until both are on screen. */
export function useSceneFrame(layer: HTMLElement | null, picture: string): SceneFrame | null {
  const [frame, setFrame] = useState<SceneFrame | null>(null)
  useEffect(() => {
    if (!layer) return
    let img: HTMLImageElement | null = null
    const ro = new ResizeObserver(() => measure())
    function measure() {
      const now = document.querySelector<HTMLImageElement>('.scene img')
      if (now !== img) {
        if (img) ro.unobserve(img)
        img = now
        if (img) ro.observe(img)
      }
      if (img && layer) setFrame(coverFrame(img.getBoundingClientRect(), { w: img.naturalWidth, h: img.naturalHeight }, layer.getBoundingClientRect()))
    }
    ro.observe(layer)
    document.addEventListener('load', measure, true)
    window.addEventListener('resize', measure)
    const raf = requestAnimationFrame(measure)
    measure()
    return () => {
      cancelAnimationFrame(raf)
      document.removeEventListener('load', measure, true)
      window.removeEventListener('resize', measure)
      ro.disconnect()
    }
  }, [layer, picture])
  return frame
}
